import axios from "axios";

const backendRoot = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const api = axios.create({
  baseURL: backendRoot ? `${backendRoot}/api` : "/api",
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuth = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/me");
    if (status === 401 && !original._retry && !isAuth) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export const login = (email, password) => api.post("/auth/login", { email, password });
export const register = (body) => api.post("/auth/register", body);
export const localSso = (body) => api.post("/auth/sso/local", body);
export const getSsoConfig = () => api.get("/auth/sso/config");
export const me = () => api.get("/auth/me", { validateStatus: (status) => status === 200 || status === 401 });
export const refreshSession = () => api.post("/auth/refresh");
export const logout = () => api.post("/auth/logout");

export const postVisitorEvent = (body) => api.post("/public/visitor-events", body);
export const submitSalesLead = (body) => api.post("/public/sales-leads", body);
export const getPublicPlans = () => api.get("/public/plans");
export const getCaptchaConfig = () => api.get("/public/captcha-config");

export const getPortfolio = (businessUnit) =>
  api.get("/portfolio", { params: businessUnit ? { business_unit: businessUnit } : {} });
export const getBusinessUnits = () => api.get("/business-units");
export const getProgram = (programId) => api.get("/program", { params: programId ? { program_id: programId } : {} });
export const getPrograms = () => api.get("/programs");
export const createAppProject = (id) => api.post(`/apps/${id}/projects`);
export const askProgram = (question) => api.post("/program/ask", { question });
export const checkProgramWave = (sourceId, waveId) => api.post("/program/waves/check", { source_id: sourceId, wave_id: waveId });
export const ingestProgramDependencies = (content, channel = "csv") => api.post("/program/dependencies/ingest", { content, channel });
export const dispatchProgramAlerts = () => api.post("/program/alerts/dispatch");
export const getApp = (id) => api.get(`/apps/${id}`);
export const authorizeMdr = (id, body) => api.post(`/apps/${id}/authorize`, body);
export const addEvidence = (id, body) => api.post(`/apps/${id}/evidence`, body);
export const ingestEvidence = (id, body) => api.post(`/apps/${id}/evidence/ingest`, body);
export const reassess = (id, body) => api.post(`/apps/${id}/reassess`, body);
export const deployApp = (id, body = {}) => api.post(`/apps/${id}/deploy`, body);
export const closeG8 = (id, body = {}) => api.post(`/apps/${id}/close-g8`, body);
export const advanceGate = (id, gateId, body) => api.post(`/apps/${id}/gates/${gateId}/advance`, body);
export const assignWave = (id, body) => api.post(`/apps/${id}/wave`, body);
export const getMdrAudit = (id) => api.get(`/apps/${id}/mdr-audit`);
export const getAuthorities = () => api.get("/policy/authorities");
export const getInputDomains = () => api.get("/policy/input-domains");
export const getEnterpriseStrategy = () => api.get("/policy/enterprise-strategy");
export const getBrdMaster = () => api.get("/policy/brd-master");
export const getRoles = () => api.get("/policy/roles");
export const updateFoundationFact = (id, factId, body) => api.put(`/apps/${id}/foundation/facts/${factId}`, body);
export const getPlatformAdmin = () => api.get("/admin/platform");
export const inspectPlatformTenant = (code) => api.get(`/admin/platform/tenants/${encodeURIComponent(code)}`);
export const getPlatformAudit = () => api.get("/admin/platform/audit");
export const getTenantAdmin = () => api.get("/admin/tenant");
export const getTenantAudit = () => api.get("/admin/tenant/audit");
export const getTenantGovernanceConfig = () => api.get("/admin/tenant/governance-config");
export const updateTenantGovernanceConfig = (body) => api.put("/admin/tenant/governance-config", body);
export const listTenantUserAuthorities = () => api.get("/admin/tenant/user-authorities");
export const updateTenantUserAuthority = (email, body) => api.put(`/admin/tenant/users/${encodeURIComponent(email)}/authority`, body);
export const getArchitectureLayers = () => api.get("/policy/architecture-layers");
export const inviteTenantUser = (body) => api.post("/admin/tenant/users", body);
export const createProgram = (body) => api.post("/programs", body);
export const updateProgram = (programId, body) => api.put(`/programs/${encodeURIComponent(programId)}`, body);
export const signProgramCharter = (programId, body = {}) => api.post(`/programs/${encodeURIComponent(programId)}/charter/sign`, body);
export const updateProgramFunding = (programId, body) => api.put(`/programs/${encodeURIComponent(programId)}/funding`, body);
export const draftFundedRoadmap = (programId, body) => api.put(`/programs/${encodeURIComponent(programId)}/funded-roadmap`, body);
export const approveFundedRoadmap = (programId, body = {}) => api.post(`/programs/${encodeURIComponent(programId)}/funded-roadmap/approve`, body);
export const deleteProgram = (programId) => api.delete(`/programs/${encodeURIComponent(programId)}`);
export const createApplication = (body) => api.post("/apps", body);
export const updateWhy = (id, body) => api.put(`/apps/${id}/why`, body);
export const updateRecommendation = (id, body) => api.put(`/apps/${id}/recommendation`, body);
export const decideG1 = (id, body) => api.post(`/apps/${id}/g1/decide`, body);
export const associateCandidate = (id, body) => api.post(`/apps/${id}/candidate`, body);
export const getAccessModel = () => api.get("/policy/access-model");
export const getMyAccess = () => api.get("/policy/access-model/me");
export const getConnectors = () => api.get("/connectors");
export const runConnector = (id) => api.post(`/connectors/${encodeURIComponent(id)}/run`);
export const runConnectorLive = (id, body) => api.post(`/connectors/${encodeURIComponent(id)}/live`, body);
export const getEnterpriseModel = () => api.get("/policy/enterprise-model");
export const getDecisionGraphSchema = () => api.get("/policy/decision-graph-schema");
export const getAppDecisionGraph = (id) => api.get(`/apps/${id}/decision-graph`);
export const listPlatformTenants = () => api.get("/admin/platform/tenants");
export const upsertPlatformTenant = (body) => api.post("/admin/platform/tenants", body);
export const deletePlatformTenant = (code) => api.delete(`/admin/platform/tenants/${encodeURIComponent(code)}`);
export const upsertPlatformOrganization = (body) => api.post("/admin/platform/organizations", body);
export const deletePlatformOrganization = (id) => api.delete(`/admin/platform/organizations/${encodeURIComponent(id)}`);
export const createBusinessUnit = (body) => api.post("/business-units", body);
